import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { useProvince } from "@/contexts/ProvinceContext";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Move, Save, X, LayoutGrid, Eye, EyeOff, GripVertical } from "lucide-react";
import MainSlider from "@/components/home/MainSlider";
import FinanceBar from "@/components/home/FinanceBar";
import WeatherBar from "@/components/home/WeatherBar";
import SmallNewsSlider from "@/components/home/SmallNewsSlider";
import ColumnistsSlider from "@/components/home/ColumnistsSlider";
import VideosGrid from "@/components/home/VideosGrid";
import VideoNewsStrip from "@/components/home/VideoNewsStrip";
import AdBanner from "@/components/home/AdBanner";
import SportsTable from "@/components/home/SportsTable";
import EventsBlock from "@/components/home/EventsBlock";
import PharmaciesBlock from "@/components/home/PharmaciesBlock";
import CityGuideBlock from "@/components/home/CityGuideBlock";
import SocialMediaBlock from "@/components/home/SocialMediaBlock";
import TodayEventsTicker from "@/components/home/TodayEventsTicker";
import LatestNewsGrid from "@/components/home/LatestNewsGrid";
import PollBlock from "@/components/home/PollBlock";

const Wrap = ({ children, dense = false }) => (
  <div className={`max-w-7xl mx-auto px-3 md:px-4 ${dense ? "py-0.5" : "py-1.5"}`}>{children}</div>
);

const BLOCK_MAP = {
  top_banner_ad: () => <Wrap dense><AdBanner slug="top-banner" /></Wrap>,
  video_news_strip: () => <Wrap dense><VideoNewsStrip /></Wrap>,
  today_events: () => <Wrap><TodayEventsTicker /></Wrap>,
  main_slider: () => <Wrap><MainSlider /></Wrap>,
  finance_bar: () => <FinanceBar />,
  weather_bar: () => <WeatherBar />,
  small_news_slider: () => <Wrap><SmallNewsSlider /></Wrap>,
  columnists: () => <Wrap><ColumnistsSlider /></Wrap>,
  videos_2x2: () => <Wrap><VideosGrid /></Wrap>,
  mid_banner_ad: () => <Wrap dense><AdBanner slug="below-slider" /></Wrap>,
  sports: () => <Wrap><SportsTable /></Wrap>,
  events: () => <Wrap><EventsBlock /></Wrap>,
  pharmacies: () => <Wrap><PharmaciesBlock /></Wrap>,
  city_guide: () => <Wrap><CityGuideBlock /></Wrap>,
  social_media: () => <Wrap><SocialMediaBlock /></Wrap>,
  active_poll: () => <Wrap><PollBlock /></Wrap>,
  latest_news_grid: () => <Wrap><LatestNewsGrid /></Wrap>,
};

const BLOCK_LABELS = {
  top_banner_ad: "Üst Banner Reklam",
  video_news_strip: "Video Haberler Şeridi",
  today_events: "Bugünün Etkinlikleri",
  main_slider: "Ana Slider",
  finance_bar: "Piyasa Barı",
  weather_bar: "Hava Durumu",
  small_news_slider: "Küçük Haber Slider",
  columnists: "Köşe Yazarları",
  videos_2x2: "Video Grid",
  mid_banner_ad: "Orta Banner Reklam",
  sports: "Spor Tablosu",
  events: "Etkinlikler",
  pharmacies: "Nöbetçi Eczaneler",
  city_guide: "Şehir Rehberi",
  social_media: "Sosyal Medya",
  active_poll: "Aktif Anket",
  latest_news_grid: "Tüm Haberler (Alt)",
};

export default function Home() {
  const { selected } = useProvince();
  const { user } = useAuth();
  const [blocks, setBlocks] = useState([]);
  const [layoutMode, setLayoutMode] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const dragFromRef = useRef(null);
  const [dragOverKey, setDragOverKey] = useState(null);

  const canEdit = user && ["super_admin", "province_admin", "editor"].includes(user?.role);

  useEffect(() => {
    api.get("/homepage-blocks").then((r) => {
      const list = (r.data.blocks || []);
      // Ensure the video_news_strip exists between top banner and main slider.
      if (!list.find((b) => b.key === "video_news_strip")) {
        const idxTop = list.findIndex((b) => b.key === "top_banner_ad");
        const idxSlider = list.findIndex((b) => b.key === "main_slider");
        const insertAt =
          idxTop >= 0 ? idxTop + 1 : idxSlider >= 0 ? idxSlider : 0;
        list.splice(insertAt, 0, {
          key: "video_news_strip",
          label: "Video Haberler Şeridi",
          is_active: true,
          sort_order: 1.5,
        });
      }
      // Ensure today_events block exists right after slider
      if (!list.find((b) => b.key === "today_events")) {
        const idxSlider = list.findIndex((b) => b.key === "main_slider");
        const insertAt = idxSlider >= 0 ? idxSlider + 1 : 1;
        const todayBlock = { key: "today_events", label: "Bugünün Etkinlikleri", is_active: true, sort_order: 2.5 };
        list.splice(insertAt, 0, todayBlock);
      }
      // Ensure the "latest news grid" tail block exists
      if (!list.find((b) => b.key === "latest_news_grid")) {
        const maxOrder = list.reduce((m, b) => Math.max(m, b.sort_order || 0), 0);
        list.push({
          key: "latest_news_grid",
          label: "Tüm Haberler (Alt Bölüm)",
          is_active: true,
          sort_order: maxOrder + 1,
        });
      }
      // Ensure the active poll block exists
      if (!list.find((b) => b.key === "active_poll")) {
        const anchor = list.findIndex((b) => b.key === "pharmacies");
        const insertAt = anchor >= 0 ? anchor + 1 : list.length;
        list.splice(insertAt, 0, {
          key: "active_poll",
          label: "Aktif Anket",
          is_active: true,
          sort_order: (list[anchor]?.sort_order || 12) + 0.5,
        });
      }
      // Normalise: sort once by sort_order so drag-drop starts from the visual state
      list.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      setBlocks(list);
    }).catch(() => {});
  }, []);

  // ─── Reorder helpers ─────────────────────────────────────────────────
  const moveBlock = (fromIdx, toIdx) => {
    if (fromIdx === toIdx || fromIdx < 0 || toIdx < 0) return;
    setBlocks((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      // Re-normalise sort_order so it reflects the new positions
      return next.map((b, i) => ({ ...b, sort_order: i + 1 }));
    });
    setDirty(true);
  };

  const onDragStart = (idx) => (e) => {
    dragFromRef.current = idx;
    try { e.dataTransfer.effectAllowed = "move"; } catch {}
  };
  const onDragOver = (key) => (e) => {
    e.preventDefault();
    if (dragOverKey !== key) setDragOverKey(key);
  };
  const onDrop = (idx) => (e) => {
    e.preventDefault();
    const from = dragFromRef.current;
    dragFromRef.current = null;
    setDragOverKey(null);
    if (from == null) return;
    moveBlock(from, idx);
  };
  const onDragEnd = () => {
    dragFromRef.current = null;
    setDragOverKey(null);
  };

  const toggleActive = (idx) => {
    setBlocks((prev) => prev.map((b, i) => i === idx ? { ...b, is_active: !b.is_active } : b));
    setDirty(true);
  };

  const saveLayout = async () => {
    setSaving(true);
    try {
      // Server accepts the full list, we send everything as-is with recomputed sort_order.
      await api.put("/homepage-blocks", { blocks: blocks.map((b, i) => ({ ...b, sort_order: i + 1 })) });
      toast.success("Anasayfa düzeni kaydedildi");
      setDirty(false);
      setLayoutMode(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const cancelLayout = () => {
    // Reload from server to discard local changes
    api.get("/homepage-blocks").then((r) => {
      const list = (r.data.blocks || []).slice();
      list.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      setBlocks(list);
      setDirty(false);
      setLayoutMode(false);
    });
  };

  const active = layoutMode ? blocks : blocks.filter((b) => b.is_active);

  return (
    <div data-testid="home-page" className="pb-2 relative">
      {/* Blocks (visible list) */}
      {active.map((b, idx) => {
        const Render = BLOCK_MAP[b.key];
        if (!Render) return null;
        // In layout mode we wrap each block with drag-drop UI and disable pointer events on inner block.
        if (layoutMode) {
          return (
            <div
              key={b.key}
              draggable
              onDragStart={onDragStart(idx)}
              onDragOver={onDragOver(b.key)}
              onDrop={onDrop(idx)}
              onDragEnd={onDragEnd}
              data-testid={`home-block-${b.key}`}
              className={`relative my-2 mx-2 md:mx-4 border-2 rounded-lg overflow-hidden transition-all ${
                dragOverKey === b.key ? "border-amber-400 shadow-lg scale-[1.005]" : b.is_active ? "border-[#e11d2a]/40" : "border-gray-300 opacity-50"
              }`}
            >
              {/* Overlay to prevent inner block interactions during layout mode */}
              <div className="absolute inset-0 z-20 bg-white/0 hover:bg-black/5 cursor-grab active:cursor-grabbing"></div>
              {/* Handle chip */}
              <div className="absolute top-2 left-2 z-30 inline-flex items-center gap-1.5 px-2 py-1 bg-black/80 text-white text-[11px] font-semibold rounded-full shadow">
                <GripVertical className="w-3 h-3" />
                {idx + 1}. {BLOCK_LABELS[b.key] || b.key}
              </div>
              {/* Visibility toggle */}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); toggleActive(idx); }}
                data-testid={`home-block-toggle-${b.key}`}
                className={`absolute top-2 right-2 z-30 inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-full shadow ${
                  b.is_active ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-gray-500 hover:bg-gray-600 text-white"
                }`}
              >
                {b.is_active ? <><Eye className="w-3 h-3" /> Görünür</> : <><EyeOff className="w-3 h-3" /> Gizli</>}
              </button>
              {/* Render block underneath overlay */}
              <div className="pointer-events-none select-none">
                <Render />
              </div>
            </div>
          );
        }
        return <Render key={b.key} />;
      })}

      {/* Floating admin edit toggle */}
      {canEdit && (
        <div className="fixed bottom-4 right-4 z-40 flex items-center gap-2" data-testid="home-layout-controls">
          {!layoutMode ? (
            <button
              type="button"
              onClick={() => setLayoutMode(true)}
              data-testid="home-layout-toggle"
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-white hover:bg-gray-50 text-gray-800 text-xs font-semibold rounded-full shadow-lg border border-gray-200"
            >
              <LayoutGrid className="w-4 h-4" /> Anasayfa Düzenle
            </button>
          ) : (
            <>
              <div className="px-3 py-2 bg-amber-50 border border-amber-300 text-amber-900 text-[11px] rounded-full shadow-lg inline-flex items-center gap-1">
                <Move className="w-3 h-3" /> Blokları sürükleyerek sıralayın
              </div>
              {dirty && (
                <button
                  type="button"
                  onClick={saveLayout}
                  disabled={saving}
                  data-testid="home-layout-save"
                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-full shadow-lg disabled:opacity-50"
                >
                  <Save className="w-4 h-4" /> {saving ? "..." : "Kaydet"}
                </button>
              )}
              <button
                type="button"
                onClick={cancelLayout}
                data-testid="home-layout-cancel"
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-white hover:bg-gray-50 text-gray-800 text-xs font-semibold rounded-full shadow-lg border border-gray-200"
              >
                <X className="w-4 h-4" /> {dirty ? "İptal" : "Çıkış"}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
